import { ReactNode } from 'react';
import { TableV2Column } from 'components/Table/TableV2';
import {
  ApplicationStage,
  CrossCampaignConversationItem
} from 'modules/conversations/types';
import {
  ConversationStatusIconAndLabel,
  hasMoreTimeRequested
} from 'modules/conversations/components';
import { MoreTimeRequestedBadge } from '../components/MoreTimeRequestedBadge';
import { Box, Typography } from '@mui/material';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import {
  SearchableConversationSortableFields,
  SearchableSortDirection
} from 'generated-graphql/types';
import { SortInput } from 'common/hooks/useTableSort';
import { ConversationFilterIds } from '../constants/enums';
import { toPercentage } from 'utils/calculations';
import { formatDateFromNowAbbreviated, formatToUSDate } from 'utils/datetime';
import { screeningStatusFilterOptions } from '../constants/screeningStatusFilterOptions';
import { statusFilterOptions } from '../constants/statusFilterOptions';
import { ScreeningStatusChip } from 'modules/conversations/components/ScreeningStatusChip';
import { stageOptions } from '../constants/stageOptions';
import { meetingOptions } from '../constants/meetingOptions';
import { IconTooltip } from 'common/ui';
import { ApplicationStageMapper } from './applicationStageMapper';
import { crossCampaignConversationsSx } from 'modules/conversations/styles';
import { getConversationTagsColumn } from './getConversationTagsColumn';
import { CalendarMeetingStatusChip } from 'modules/calendar/components/CalendarMeetingChip';
import { mapType } from 'utils/generatedCampaignMappers';
import { bookedMeetingStatuses } from '../constants/statuses';

type Params = {
  openConversationDetailsDrawer: (conversationId: string) => void;
  onOpenCampaign: (campaignId: string) => void;
  tagsFilterComponent: ReactNode;
  onTagClick: (tag: string) => void;
  selectedTags: string[];
  sort?: SortInput<SearchableConversationSortableFields>;
};

const isBooked = ({ meetingBookingStatus }: CrossCampaignConversationItem) =>
  !!meetingBookingStatus &&
  bookedMeetingStatuses.includes(meetingBookingStatus);

const CELL_PADDING_X_PX = 32;

// The design widths are the floor, where the table scrolls sideways. Wider
// screens share the extra space in the same ratio, so no one column takes it.
// The table library grows each cell from its padding, not from zero, so the
// growth is the content width; otherwise narrow columns grow fastest.
const designWidth = (px: number) => ({
  minWidth: `${px}px`,
  grow: px - CELL_PADDING_X_PX
});

/** Either name alone is enough; only a candidate with neither reads "Unknown". */
const participantName = ({
  participantFirstName,
  participantLastName
}: CrossCampaignConversationItem): string =>
  [participantFirstName, participantLastName].filter(Boolean).join(' ') ||
  'Unknown';

/**
 * Columns for the cross-campaign conversations tab, in design order.
 *
 * Campaign is not sortable. The index holds `campaignId`, and the title is a
 * nested resolver run after the page comes back, so the only sort available
 * orders by opaque id — a sort arrow that promises A-Z and delivers UUIDs.
 * See docs/adr/0003-cross-campaign-conversations-list.md.
 */
export const getCrossCampaignConversationColumns = ({
  openConversationDetailsDrawer,
  onOpenCampaign,
  tagsFilterComponent,
  onTagClick,
  selectedTags,
  sort
}: Params): TableV2Column<CrossCampaignConversationItem>[] => {
  // The sorted column shows its direction beside the sort icon, as the design
  // does. The table library draws one icon for every column, so it cannot.
  const sortableName = (
    label: string,
    field: SearchableConversationSortableFields
  ): ReactNode =>
    sort?.field === field ? (
      <Box sx={crossCampaignConversationsSx.sortedHeader}>
        <span>{label}</span>
        {sort.direction === SearchableSortDirection.Asc ? (
          <ArrowUpward data-testid="sort-direction-asc" />
        ) : (
          <ArrowDownward data-testid="sort-direction-desc" />
        )}
      </Box>
    ) : (
      label
    );

  const stageLabel = (row: CrossCampaignConversationItem) =>
    !row.applicationStage
      ? ''
      : ApplicationStageMapper[row.applicationStage as ApplicationStage];

  return [
    {
      cell: (row) => (
        <ConversationStatusIconAndLabel
          status={row.status}
          needsReview={!!row.needsReview}
          unresolvedNeedsReviewReasons={row.unresolvedNeedsReviewReasons}
          errorCode={row.errorCode}
          campaignType={mapType(row.campaign?.type)}
          meetingBookingStatus={row.meetingBookingStatus}
        />
      ),
      filterable: true,
      filterId: ConversationFilterIds.status,
      filterOptions: statusFilterOptions,
      center: true,
      width: '52px'
    },
    {
      name: sortableName(
        'Name',
        SearchableConversationSortableFields.ParticipantFirstName
      ),
      cell: (row) => (
        <Typography
          variant="body1"
          color="primary.main"
          sx={crossCampaignConversationsSx.linkCell}
          onClick={() => openConversationDetailsDrawer(row.id)}
          component="a"
        >
          {participantName(row)}
        </Typography>
      ),
      sortable: true,
      id: SearchableConversationSortableFields.ParticipantFirstName,
      sortField: SearchableConversationSortableFields.ParticipantFirstName,
      ...designWidth(270)
    },
    {
      name: 'Campaign',
      cell: (row) => {
        const title = row.campaign?.title;

        if (!title || !row.campaignId) {
          return (
            <Typography sx={crossCampaignConversationsSx.textCell}>
              -
            </Typography>
          );
        }

        const campaignId = row.campaignId;

        return (
          <Typography
            variant="body1"
            color="primary.main"
            sx={crossCampaignConversationsSx.linkCell}
            onClick={() => onOpenCampaign(campaignId)}
            component="a"
          >
            {title}
          </Typography>
        );
      },
      ...designWidth(270)
    },
    {
      name: sortableName(
        'Last active',
        SearchableConversationSortableFields.UpdatedAt
      ),
      selector: (row) => formatDateFromNowAbbreviated(row.updatedAt),
      cell: (row) => (
        <Typography sx={crossCampaignConversationsSx.textCell}>
          {formatDateFromNowAbbreviated(row.updatedAt)}
        </Typography>
      ),
      sortable: true,
      id: SearchableConversationSortableFields.UpdatedAt,
      sortField: SearchableConversationSortableFields.UpdatedAt,
      ...designWidth(180)
    },
    {
      name: 'Status',
      cell: (row) => (
        <ScreeningStatusChip
          status={row.status}
          campaignType={mapType(row.campaign?.type)}
          meetingBookingStatus={row.meetingBookingStatus}
        />
      ),
      filterable: true,
      filterId: ConversationFilterIds.screeningStatus,
      filterOptions: screeningStatusFilterOptions,
      id: ConversationFilterIds.screeningStatus,
      ...designWidth(150)
    },
    {
      name: sortableName(
        'Score',
        SearchableConversationSortableFields.PreferredAnswersScore
      ),
      sortable: true,
      cell: (row) => (
        <Typography sx={crossCampaignConversationsSx.textCell}>
          {row.preferredAnswersScore !== null &&
          row.preferredAnswersScore !== undefined
            ? toPercentage(row.preferredAnswersScore)
            : '-'}
        </Typography>
      ),
      id: SearchableConversationSortableFields.PreferredAnswersScore,
      sortField: SearchableConversationSortableFields.PreferredAnswersScore,
      width: '96px'
    },
    {
      name: 'Meeting status',
      // A booking wins: calendar-service can leave MORE_TIME_REQUESTED open
      // after a booking when its re-read of the conversation fails.
      cell: (row) =>
        !isBooked(row) &&
        hasMoreTimeRequested(row.unresolvedNeedsReviewReasons) ? (
          <MoreTimeRequestedBadge />
        ) : (
          <CalendarMeetingStatusChip
            status={row.meetingBookingStatus}
            showIcon
          />
        ),
      filterable: true,
      filterId: ConversationFilterIds.meetingBookingStatus,
      id: ConversationFilterIds.meetingBookingStatus,
      filterOptions: meetingOptions,
      ...designWidth(165)
    },
    {
      name: 'Stage',
      cell: (row) =>
        row.applicationStageUpdates?.items.length ? (
          <IconTooltip
            title={`Updated ${formatToUSDate(
              row.applicationStageUpdates.items[
                row.applicationStageUpdates.items.length - 1
              ]?.stageUpdatedAt || ''
            )}`}
          >
            <Box>
              <Typography sx={crossCampaignConversationsSx.textCell}>
                {stageLabel(row)}
              </Typography>
            </Box>
          </IconTooltip>
        ) : (
          <Typography sx={crossCampaignConversationsSx.textCell}>
            {stageLabel(row)}
          </Typography>
        ),
      filterable: true,
      filterId: ConversationFilterIds.applicationStage,
      id: ConversationFilterIds.applicationStage,
      filterOptions: stageOptions,
      ...designWidth(111)
    },
    {
      name: sortableName('ID', SearchableConversationSortableFields.ExternalId),
      cell: (row) => (
        <Typography sx={crossCampaignConversationsSx.textCell}>
          {row.externalId || '-'}
        </Typography>
      ),
      sortable: true,
      id: SearchableConversationSortableFields.ExternalId,
      sortField: SearchableConversationSortableFields.ExternalId,
      ...designWidth(128)
    },
    {
      ...getConversationTagsColumn<CrossCampaignConversationItem>(
        tagsFilterComponent,
        onTagClick,
        selectedTags
      ),
      ...designWidth(350)
    }
  ];
};
